import type { JSX } from 'react'
import { Spinner } from '@astryxdesign/core/Spinner'
import { storyCoverClass } from './publishedStoryLinks'

interface BookCoverProps {
  coverUrl: string | null
  title: string
  authorName: string
  size?: 'sm' | 'md' | 'lg'
  placeholderIndex?: number
  isLoading?: boolean
  className?: string
}

/**
 * A 2:3 paperback front: the generated image (or the feed's gradient placeholder) with a spine
 * shadow and the title and pen name on a scrim along the bottom. Used at three sizes: rows in
 * the feed, the story page header, and the publish dialog's cover step.
 */
export default function BookCover({
  coverUrl,
  title,
  authorName,
  size = 'sm',
  placeholderIndex = 0,
  isLoading = false,
  className = ''
}: BookCoverProps): JSX.Element {
  const placeholder = coverUrl ? '' : ` ${storyCoverClass(placeholderIndex)} book-cover--placeholder`
  return (
    <figure
      className={`book-cover book-cover--${size}${placeholder} ${className}`.trim()}
      role="img"
      aria-label={`Cover of ${title}`}
      style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : undefined}
    >
      {isLoading ? (
        <span className="book-cover-loading" role="status">
          <Spinner size="md" shade="onMedia" aria-hidden="true" />
          <span>Generating…</span>
        </span>
      ) : (
        !coverUrl && (
          <figcaption className="book-cover-caption">
            <span className="book-cover-title">{title}</span>
            <span className="book-cover-author">{authorName}</span>
          </figcaption>
        )
      )}
    </figure>
  )
}
